import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { toggleHiddenOnPharoahApi } from '../../../services/api';
import { setSnackbar } from '../../../redux/messageSlice';
import { updateState } from '../../../redux/commonSlice';
import { KANBAN_STAGES } from '../../../constants/constants';

const DEFAULT_ERROR = 'Failed to update project visibility';

// The API's own messages name the product ("Event hidden on Pharoah"). This feature
// is presented to users purely in terms of the dashboard, so the success copy is
// written here rather than echoed from the response, and any message we do surface
// from the server is scrubbed of the product name first.
const HIDDEN_MESSAGE = 'Project hidden from dashboard';
const VISIBLE_MESSAGE = 'Project visible on dashboard';
const scrubProductName = (text) =>
  typeof text === 'string' ? text.replace(/pharoah|pharaoh/gi, 'dashboard') : text;

/**
 * Hide / show a project on the dashboard — PUT /api/v1/events/toggle_pharoah_visibility.
 * (The endpoint keeps the backend's name; nothing user-facing does.)
 *
 * Hidden projects are filtered out server-side of the board, the project list and
 * board_summary for every role, so on success the card is removed from the
 * cached board data and the stage count decremented in place rather than refetching
 * (the server agrees on the next load). Undo — the same call with the flag flipped —
 * puts the project back, which does need a refetch, so board queries are invalidated
 * whenever a project becomes visible again.
 *
 * Returns:
 *   toggleVisibility({ eventId, hidden, statusTypeOrder }, { onSuccess, onError })
 *   isToggling — id of the event currently being toggled, or null
 */
const usePharoahVisibility = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isToggling, setIsToggling] = useState(null);

  // Drop the event from every cached board column / summary preview and decrement
  // the owning stage's total. Applied to both the default and hidden=true variants
  // of those queries — the project belongs in exactly one of them, and it has just
  // moved out of whichever one is on screen.
  const removeFromBoardCache = useCallback(
    (eventId, statusTypeOrder) => {
      queryClient.setQueriesData(['kanban-column'], (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => {
            const events = page?.data?.data?.events;
            if (!Array.isArray(events)) return page;
            return {
              ...page,
              data: {
                ...page.data,
                data: { ...page.data.data, events: events.filter((e) => e.id !== eventId) },
              },
            };
          }),
        };
      });

      const stageKey = KANBAN_STAGES.find((s) => s.id === statusTypeOrder)?.key;
      queryClient.setQueriesData(['kanban-board-summary'], (old) => {
        const summary = old?.data?.data?.summary;
        if (!summary) return old;
        const nextSummary = {};
        Object.keys(summary).forEach((key) => {
          const stage = summary[key];
          const events = Array.isArray(stage?.events) ? stage.events : [];
          const hadEvent = events.some((e) => e.id === eventId);
          // The preview list only carries the first few events, so a card can be on
          // the board without appearing here — fall back to the stage the project
          // reports it is in so the count still moves.
          const shouldDecrement = hadEvent || (!!stageKey && key === stageKey);
          nextSummary[key] = {
            ...stage,
            total: shouldDecrement ? Math.max((stage?.total ?? 0) - 1, 0) : stage?.total,
            events: events.filter((e) => e.id !== eventId),
          };
        });
        return { ...old, data: { ...old.data, data: { ...old.data.data, summary: nextSummary } } };
      });

      // The hidden review queue is fetched imperatively (not through react-query),
      // so its own hook prunes the row; nothing to do here.
    },
    [queryClient]
  );

  const invalidateBoard = useCallback(() => {
    queryClient.invalidateQueries(['kanban-board-summary']);
    queryClient.invalidateQueries(['kanban-column']);
    queryClient.invalidateQueries(['kanban-projects']);
    queryClient.invalidateQueries(['quick-turn-lookup']);
  }, [queryClient]);

  const toggleVisibility = useCallback(
    async ({ eventId, hidden, statusTypeOrder }, { onSuccess, onError } = {}) => {
      if (eventId == null) return;
      setIsToggling(eventId);
      try {
        // `hidden` is required and must be explicit — the API 400s on a missing value.
        const response = await toggleHiddenOnPharoahApi({ event_id: eventId, hidden: !!hidden });
        if (response?.data?.http_code === 200) {
          dispatch(
            setSnackbar({
              flag: true,
              type: 'success',
              message: hidden ? HIDDEN_MESSAGE : VISIBLE_MESSAGE,
            })
          );
          if (hidden) {
            removeFromBoardCache(eventId, statusTypeOrder);
            // A project just entered the hidden queue (a first hide, or an undo of a
            // "show"). The queue is fetched imperatively, so signal it to reload.
            dispatch(updateState({ pharoah_hidden_list_revision: Date.now() }));
          } else {
            // Back on the board — the cached lists have no row to patch in, so refetch.
            invalidateBoard();
          }
          onSuccess && onSuccess(response?.data?.data);
        }
      } catch (error) {
        const message = scrubProductName(error?.response?.data?.message) || DEFAULT_ERROR;
        dispatch(setSnackbar({ flag: true, type: 'error', message }));
        onError && onError(error);
      } finally {
        setIsToggling(null);
      }
    },
    [dispatch, removeFromBoardCache, invalidateBoard]
  );

  return { toggleVisibility, isToggling, invalidateBoard };
};

export default usePharoahVisibility;
